// Exported for testing
export const LOCAL_STORAGE_KEY = 'pos.rn.preferences';
export const DEFAULT_PREFERENCES = {
  general: {
    locale: 'en-US',
    unitSystem: 'Metric',
    paperUsage: 'EquivalentPages' //'EquivalentPages' or 'SquareArea'
  }
};

/**
 * @ngdoc service
 * @name rhino.components.preferences.service:rnPreferences
 * @description Provides for managing the user's preferences
 */

/**
 * @ngdoc event
 * @name rnPreferencesUpdated
 * @eventOf rhino.components.preferences.service:rnPreferences
 * @description emitted on `$rootScope` when preferences are updated
 */
export const EVENT_PREFERENCES_UPDATED = 'rnPreferencesUpdated';

export const name = 'rnPreferences';
export default function rnPreferences($q, $log, rnHttp, rnClient, rnSession, $rootScope, rnLocalStorage, $cacheFactory, rnAUTH_EVENTS, $window) {
  //noinspection BadExpressionStatementJS
  'ngInject';

  const PORTAL_SERVICE_NAME = 'portal_service';
  const PORTAL_PREFERENCES_PATH = 'preferences';

  let preferencesCache = $cacheFactory('rnPreferences');
  let queue = [];

  let service = {
    getAll,
    setAll,
    getSingle,
    setSingle,
    setSingleLegacy,
    getSingleObject,
    setSingleObject,
    setSingleObjectLegacy,
    setCategory,
    getPrefObjectHelper,
    getChoices,
    deleteSingle,
    deleteAll
  };

  activate();
  return service;

  ////////////////

  function activate() {
    $rootScope.$on(rnAUTH_EVENTS.authenticated, invalidateCache);
    $rootScope.$on(rnAUTH_EVENTS.notAuthenticated, invalidateCache);
  }

  function invalidateCache() {
    preferencesCache.removeAll();
  }

  function synchronize(myfunction, param1, param2, param3) {
    let deferred = $q.defer();
    queue.push({ myFunction: myfunction, param1: param1, param2: param2, param3: param3, promise: deferred});
    if (queue.length === 1) {
      runQueueItem();
    }
    return deferred.promise;
  }

  function runQueueItem() {
    queue[0].myFunction.call(this, queue[0].param1, queue[0].param2, queue[0].param3).then(resp => {
      queue[0].promise.resolve(resp);
      queue.shift();
      if (queue.length > 0) {
        runQueueItem();
      }
    }).catch(() => {
      queue[0].promise.reject();
    });
  }

  /**
   * @ngdoc function
   * @name getAll
   * @methodOf rhino.components.preferences.service:rnPreferences
   * @description Gets all preferences, falling back to default preferences on error or
   * missing defaults.
   *
   * @returns {IPromise<preferences>} a promise containing the preferences
   */
  function getAll(cache = true) {
    return synchronize(_getAll, cache);
  }

  function _getAll(cache = true) {
    if (cache) {
      let cachedPreferences = preferencesCache.get('currentPreferences');
      if (cachedPreferences) {
        return $q.resolve(cachedPreferences);
      }
    }
    return rnSession.isAuthenticated().then(auth => {
      if (auth) {
        return rnHttp.get(PORTAL_SERVICE_NAME, PORTAL_PREFERENCES_PATH, {version: 2})
          .then(resp => {
            let prefs = _.get(resp, 'data');
            // If for some reason we don't have general section (I.E. Device session) we'll fake defaults
            if (!_.get(prefs, 'general')) {
              prefs = angular.merge(prefs, DEFAULT_PREFERENCES);
              return _setAll(prefs);
            }
            prefs = preferencesUpdated(prefs, true);
            return prefs;
          })
          .catch(() => {
            $log.debug('Unable to find general user preferences (they may have not been set yet).  Returning default preferences.');
            // Invalidate the cache so that it tries again next time.
            preferencesCache.removeAll();
            return _setAll(angular.merge({}, DEFAULT_PREFERENCES));
          });
      }

      let prefs = rnLocalStorage.getObject(LOCAL_STORAGE_KEY);
      if (!prefs) {
        prefs = angular.merge({}, DEFAULT_PREFERENCES);
      }
      prefs = preferencesUpdated(prefs, true);
      return prefs;
    });
  }

  /**
   * @ngdoc function
   * @name setAll
   * @methodOf rhino.components.preferences.service:rnPreferences
   * @description Sets all preferences and emits `rnPreferencesUpdated` on `$rootScope` if successfully updated.
   *
   * @param {object} preferences to set
   * @returns {IPromise<object>} updated preferences
   */
  function setAll(preferences) {
    return synchronize(_setAll, preferences);
  }

  function _setAll(preferences) {
    return rnSession.isAuthenticated().then(auth => {
      if (auth) {
        return rnHttp.put(PORTAL_SERVICE_NAME, PORTAL_PREFERENCES_PATH, preferences, { version: 2, headers: {'Content-Type':'application/json'}})
          .then(response => {
            let updatedPrefs = preferencesUpdated(response.data);
            return updatedPrefs;
          });
      } else {
        let updatedPrefs = preferencesUpdated(preferences);
        return updatedPrefs;
      }
    });
  }

  function setSingleObject(category, key, valueObj) {
    return setSingle(category, key, valueObj);
  }

  function setSingleObjectLegacy(category, key, valueObj) {
    return setSingleLegacy(category, key, JSON.stringify(valueObj));
  }
  function setSingleLegacy(appId, name, value) {
    return synchronize(_setSingleLegacy, appId, name, value);
  }

  function _setSingleLegacy(appId, name, value) {
    let prefsPatch = _.set({}, [appId, name], value);

    let deferred = $q.defer();
    rnSession.isAuthenticated().then(auth => {
      if (auth) {
        rnHttp.patch(PORTAL_SERVICE_NAME, PORTAL_PREFERENCES_PATH, prefsPatch, {version: 1})
          .then(response => {
            let updatedPrefs = preferencesUpdated(response.data);
            deferred.resolve(updatedPrefs);
          });
      } else {
        let localStoragePrefs = rnLocalStorage.getObject(LOCAL_STORAGE_KEY) || {};
        angular.merge(localStoragePrefs, prefsPatch);
        let updatedPrefs = preferencesUpdated(localStoragePrefs);
        deferred.resolve(updatedPrefs);
      }
    });
    return deferred.promise;
  }

  function getSingleObject(appId, name) {
    return getAll()
      .then(prefs => {
        return _.get(prefs, [appId, name]);
      });
  }

  function getSingle(appId, name) {
    return getAll()
      .then(prefs => {
        let pref = _.get(prefs, [appId, name]);
        return pref;
      });
  }

  function getPrefObjectHelper(prefs, appId, name) {
    let obj;
    if (prefs && prefs[appId] && prefs[appId][name]) {
      obj = prefs[appId][name];
      if (!(obj instanceof Object)) {
        obj = JSON.parse(obj);
      }
    }
    return obj;
  }

  /**
   * @ngdoc function
   * @name setSingle
   * @methodOf rhino.components.preferences.service:rnPreferences
   * @description Sets a single preference and emits `rnPreferencesUpdated` on `$rootScope` if successfully updated.
   *
   * @param {string} appId id of the application containing the preference ('general' for general preferences)
   * @param {string} name preference name
   * @param {value} value preference value
   * @returns {IPromise<object>} complete set of updated preferences
   */
  function setSingle(category, path, value) {
    return synchronize(_setSingle, category, path, value);
  }

  function _setSingle(category, path, value) {
    let deferred = $q.defer();
    _getAll(true).then(prefs => {
      rnSession.isAuthenticated().then(auth => {
        let prefsToUpdate = _.set({}, [path], value);
        let currentPrefs = _.get(prefs, category, {});
        let prefsPatch = angular.merge(currentPrefs, prefsToUpdate);
        if (auth) {
          rnHttp.patch(PORTAL_SERVICE_NAME, `${PORTAL_PREFERENCES_PATH}/${category}`, prefsPatch, { version: 2, headers: {'Content-Type':'application/json'}})
            .then(response => {
              let updatedPrefs = preferencesUpdated(response.data);
              deferred.resolve(updatedPrefs);
            });
        } else {
          prefsPatch = _.set({}, [category, path], value);
          let localStoragePrefs = rnLocalStorage.getObject(LOCAL_STORAGE_KEY) || {};
          angular.merge(localStoragePrefs, prefsPatch);
          let updatedPrefs = preferencesUpdated(localStoragePrefs);
          deferred.resolve(updatedPrefs);
        }
      });

    });
    return deferred.promise;
  }

  function setCategory(category, categoryPrefs) {
    let deferred = $q.defer();
    _getAll(true).then(prefs => {
      rnSession.isAuthenticated().then(auth => {
        let cachedPreferences = preferencesCache.get('currentPreferences');
        let currentPrefs = _.get(cachedPreferences, category, {});

        let prefsPatch = angular.merge(currentPrefs, categoryPrefs);
        if (auth) {
          rnHttp.patch(PORTAL_SERVICE_NAME, `${PORTAL_PREFERENCES_PATH}/${category}`, prefsPatch, {
            version: 2,
            headers: {'Content-Type': 'application/json'}
          })
            .then(response => {
              let updatedPrefs = preferencesUpdated(response.data);
              deferred.resolve(updatedPrefs);
            });
        } else {
          prefsPatch = _.set({}, [category], prefs);
          let localStoragePrefs = rnLocalStorage.getObject(LOCAL_STORAGE_KEY) || {};
          angular.merge(localStoragePrefs, prefsPatch);
          let updatedPrefs = preferencesUpdated(localStoragePrefs);
          deferred.resolve(updatedPrefs);
        }
      });
    });
    return deferred.promise;
  }

  function deleteSingle(appId, name) {
    return synchronize(_deleteSingle, appId, name);
  }

  function _deleteSingle(appId, name) {
    return rnSession.isAuthenticated().then(auth => {
      if (auth) {
        let path = 'preferences/';
        path += appId.indexOf('.') >= 0 ? `["${appId}"]` : appId;
        path += name.indexOf('.') >= 0 ? `["${name}"]` : `.${name}`;

        return rnHttp.delete('portal_service', encodeURI(path), { version: 2, headers: {'Content-Type':'application/json'} })
          .then(resp => {
            return preferencesUpdated(resp.data);
          });
      }
    });
  }

  function deleteAll() {
    return synchronize(_deleteAll);
  }

  function _deleteAll() {
    return rnSession.isAuthenticated().then(auth => {
      if (auth) {
        return rnHttp.delete('portal_service', 'preferences', { version: 2 })
          .then(resp => {
            return preferencesUpdated(resp.data);
          });
      }
    });
  }

  function preferencesUpdated(preferences, suppressEvent) {
    // Update local storage for when unauthenticated
    rnLocalStorage.setObject(LOCAL_STORAGE_KEY, preferences);

    preferencesCache.put('currentPreferences', preferences);
    if(!suppressEvent) {
      $rootScope.$emit(EVENT_PREFERENCES_UPDATED, preferences);
    }
    return preferences;
  }

  function getChoices(appId, preferenceName) {
    let config = {
      cache: true,
      params: {
        appId: appId,
        name: preferenceName
      }
    };

    return rnHttp.get(PORTAL_SERVICE_NAME, 'preferences/choices', config)
      .then(function (response) {
        return response.data;
      });
  }
}
